import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ContractSmsPanel from '../components/ContractSmsPanel';

// ─────────────────────────────────────────────
// 공통 유틸 함수 (ContractEdit.js와 동일하게 유지할 것)
// ─────────────────────────────────────────────

// 날짜에서 시간 부분 제거 → "2026-05-06T00:00:00Z" → "2026-05-06"
const fmtDate = (d) => (d ? String(d).split('T')[0] : '');

// 숫자에 천단위 콤마 → 300000000 → "300,000,000"
const fmtMoney = (n) => (Number(n) || 0).toLocaleString('ko-KR');

// 날짜를 표시해야 하는 항목인지 판단
// 계약금/중도금/잔금 → 날짜 O / 현보증금 등 → 날짜 X
const needDate = (desc = '') => /계약금|중도금|잔금/.test(desc);

// payments 배열의 금액 총합 (= 매매금액)
const sumPayments = (payments = []) =>
  payments.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

// 객체/배열 혼재 대응 → 항상 배열로 반환
// (기존 데이터는 객체 1개, 신규 데이터는 배열이므로 양쪽 모두 처리)
const toArray = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'object' && Object.keys(v).length === 0) return [];
  return [v];
};

// 전화번호 자동 하이픈
// 01012345678 → 010-1234-5678 / 028785989 → 02-878-5989
const fmtPhone = (v = '') => {
  const n = String(v).replace(/[^0-9]/g, '').slice(0, 11);
  if (!n) return '';
  if (n.startsWith('02')) {
    // 서울 지역번호 02: 2-3(또는 4)-4
    if (n.length <= 2) return n;
    if (n.length <= 5) return `${n.slice(0, 2)}-${n.slice(2)}`;
    if (n.length <= 9) return `${n.slice(0, 2)}-${n.slice(2, 5)}-${n.slice(5)}`;
    return `${n.slice(0, 2)}-${n.slice(2, 6)}-${n.slice(6, 10)}`;
  }
  // 그 외: 3-3(또는 4)-4
  if (n.length <= 3) return n;
  if (n.length <= 7) return `${n.slice(0, 3)}-${n.slice(3)}`;
  if (n.length <= 10) return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}`;
  return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
};

// 항목 목록에서 제외할 항목 (임차금액 행에서 이미 표시하므로 중복 방지)
const isDupRentItem = (desc = '') =>
  /보증금|월세|차임/.test(desc) && !/현보증금|승계/.test(desc);

export default function ContractDetail() {
  const { id } = useParams();

  const [contract, setContract] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 원본 이미지 보기용: null이면 닫힘, 숫자면 해당 페이지 인덱스를 전체화면으로 표시
  const [viewerIdx, setViewerIdx] = useState(null);

  // ─── 로그인 사용자 정보 조회 (등급 확인용) ───
  const checkAdmin = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserData(response.data);
      setIsAdmin(response.data?.grade === 'admin');
    } catch (error) {
      console.log('[ContractDetail] 관리자 확인 실패');
    }
  };

  // ─── 계약서 상세 조회 ───
  const fetchContract = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('로그인이 필요합니다');
        setLoading(false);
        return;
      }

      const response = await axios.get(`/api/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setContract(response.data.data);
      }
    } catch (error) {
      console.error('[ContractDetail] 에러:', error.message);
      alert('계약서 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAdmin();
    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ─── 삭제 (관리자만) ───
  const handleDelete = async () => {
    if (!isAdmin) {
      alert('관리자만 삭제할 수 있습니다');
      return;
    }
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        alert('삭제되었습니다');
        window.location.href = '/contracts/list';
      }
    } catch (error) {
      alert('삭제 실패: ' + error.message);
    }
  };

  if (loading) return <div style={{ padding: '20px' }}>로딩 중...</div>;
  if (!contract) return <div style={{ padding: '20px' }}>계약서를 찾을 수 없습니다</div>;

  const isSale = contract.contract_type === 'SALE';
  const isRent = contract.contract_type === 'RENT_TENANT';

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      {/* ─── 헤더 + 버튼 ─── */}
      {/* 스크롤해도 상단에 고정되는 헤더 (버튼 접근성 확보) */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px',
      }}>
        <h1 style={{ fontSize: '20px', margin: 0 }}>📄 계약서 상세</h1>
        {/* 버튼 묶음은 화면 우상단에 고정 — 스크롤과 무관하게 항상 접근 가능 */}
        <div style={{
          display: 'flex', gap: '8px',
          position: 'fixed', top: '130px', right: '20px', zIndex: 1000,
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: '8px', borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          {/* 목록으로: admin, premium 모두 */}
          {(userData?.grade === 'admin' || userData?.grade === 'premium') && (
            <button
              onClick={() => (window.location.href = '/contracts/list')}
              style={{
                backgroundColor: '#2196F3', color: 'white', border: 'none',
                padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
              }}
            >
              👈 목록으로
            </button>
          )}

          {/* 수정: admin, premium 모두 가능 */}
          {(userData?.grade === 'admin' || userData?.grade === 'premium') && (
            <button
              onClick={() => (window.location.href = `/contracts/${id}/edit`)}
              style={{
                backgroundColor: '#FF9800', color: 'white', border: 'none',
                padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
              }}
            >
              ✏️ 수정
            </button>
          )}

          {/* 삭제: admin만 */}
          {isAdmin && (
            <button
              onClick={handleDelete}
              style={{
                backgroundColor: '#f44336', color: 'white', border: 'none',
                padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
              }}
            >
              🗑️ 삭제
            </button>
          )}
        </div>
      </div>

      <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px' }}>
        {/* ─── 계약 유형 ─── */}
        <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
          <strong>📌 계약 유형:</strong>
          {isSale && ' 🏠 매매'}
          {isRent && ' 🔑 임차'}
        </div>

        {/* [CT_WARN_V1] 확인 필요 사유 — 백엔드 services/contractCheck.js warningsOf() */}
        {Array.isArray(contract.warnings) && contract.warnings.length > 0 && (
          <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffe08a', borderRadius: '4px', color: '#6b5200' }}>
            <strong>⚠️ 확인 필요</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
              {contract.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {/* ─── 소재지 ─── */}
        <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
          <strong>📍 소재지:</strong>
          <div>{contract.location_full_address}</div>
        </div>

        {/* ─── 계약 날짜 ─── */}
        <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
          <strong>📅 계약 날짜:</strong> {fmtDate(contract.contract_date)}
        </div>

        {/* ─── 당사자 정보 (임대인 / 임차인 / 중개사무소) ───
            landlord_info, tenant_info, broker_info 는 배열로 저장됨.
            기존 데이터(객체 1개)는 toArray()가 배열로 감싸 처리한다. */}
        {[
          { key: 'landlord_info', label: isSale ? '매도인' : '임대인', icon: '👤', isBroker: false },
          { key: 'tenant_info',   label: isSale ? '매수인' : '임차인', icon: '👤', isBroker: false },
          { key: 'broker_info',   label: '중개사무소',                icon: '🤝', isBroker: true  },
        ].map(({ key, label, icon, isBroker }) => {
          const list = toArray(contract[key]);
          if (list.length === 0) return null;

          return (
            <div key={key} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
              <strong>{icon} {label}:</strong>

              {/* p = 당사자 1명 (또는 중개사무소 1곳) */}
              {list.map((p, i) => (
                <div key={i} style={{ fontSize: '13px', marginTop: '8px', paddingLeft: '4px' }}>
                  {/* 관계(role) + 성명 + 연락처 */}
                  <div>
                    {p.role && (
                      <span style={{ color: '#888', marginRight: '4px' }}>[{p.role}]</span>
                    )}
                    <strong>{p.name}</strong>
                    {p.phone && ` (${fmtPhone(p.phone)})`}
                  </div>

                  {/* 주소 — 당사자는 거주지, 중개사무소는 사무소 소재지 */}
                  {p.address && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {isBroker ? '소재지' : '주소'}: {p.address}
                    </div>
                  )}

                  {/* 중개사무소 전용: 등록번호 */}
                  {p.registrationNo && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      등록번호: {p.registrationNo}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        {/* ─── 금액 정보 (표 형식) ─── */}
        {contract.amount_info && (
          <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
            <strong>💰 금액 정보:</strong>
            <table style={{ width: '100%', marginTop: '10px', borderCollapse: 'collapse', fontSize: '14px' }}>
              <tbody>
                {/* [매매] 매매금액 = payments 합계 */}
                {isSale && (
                  <tr style={{ backgroundColor: '#e3f2fd' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold', width: '110px' }}>매매금액</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                      {fmtMoney(sumPayments(contract.amount_info?.payments))} 원
                    </td>
                    <td style={{ padding: '8px', width: '110px' }}></td>
                  </tr>
                )}

                {/* [임차] 임차금액 = 보증금 / 월세 (월세 없으면 보증금만) */}
                {isRent && (
                  <tr style={{ backgroundColor: '#e8f5e9' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold', width: '110px' }}>임차금액</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                      {fmtMoney(contract.amount_info?.depositAmount)}
                      {contract.amount_info?.monthlyRent > 0
                        ? ` / ${fmtMoney(contract.amount_info.monthlyRent)}원`
                        : ''}
                    </td>
                    <td style={{ padding: '8px', width: '110px' }}></td>
                  </tr>
                )}

                {/* [임차] 임차기간 = 시작일 ~ 종료일 */}
                {isRent && contract.contract_start_date && (
                  <tr style={{ backgroundColor: '#e8f5e9' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold' }}>임차기간</td>
                    <td style={{ padding: '8px', textAlign: 'right' }} colSpan={2}>
                      {fmtDate(contract.contract_start_date)} ~ {fmtDate(contract.contract_end_date)}
                    </td>
                  </tr>
                )}

                {/* [공통] 계약금 / 중도금 / 현보증금 / 잔금
                    임차일 때 보증금·월차임은 위 '임차금액' 행에 이미 있으므로 제외 */}
                {contract.amount_info?.payments
                  ?.filter((p) => !(isRent && isDupRentItem(p.description)))
                  .map((p, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>{p.description}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{fmtMoney(p.amount)} 원</td>
                      {/* 계약금/중도금/잔금만 날짜 표시 */}
                      <td style={{ padding: '8px', color: '#666', fontSize: '13px' }}>
                        {needDate(p.description) ? fmtDate(p.date) : ''}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ─── 특약 ─── */}
        {contract.special_clauses && (
          <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
            <strong>⚠️ 특약:</strong>
            <div style={{ fontSize: '12px', marginTop: '5px', whiteSpace: 'pre-wrap' }}>
              {contract.special_clauses}
            </div>
          </div>
        )}
        {/* ─── 계약서 원본 이미지 ───
            image_url 은 백엔드에서 배열로 정규화되어 내려온다.
            썸네일 클릭 시 viewerIdx 를 세팅해 전체화면 뷰어를 연다. */}
        {Array.isArray(contract.image_url) && contract.image_url.length > 0 && (
          <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
            <strong>🖼️ 계약서 원본 ({contract.image_url.length}장):</strong>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
              {contract.image_url.map((src, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <img
                    src={src}
                    alt={'계약서 ' + (i + 1) + '페이지'}
                    onClick={() => setViewerIdx(i)}
                    style={{
                      width: '110px', height: '150px', objectFit: 'cover',
                      border: '1px solid #ddd', borderRadius: '4px',
                      cursor: 'pointer', backgroundColor: '#fafafa',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                    {i + 1}페이지
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── SMS 발송 설정 (admin, premium) ─── */}
        {(userData?.grade === 'admin' || userData?.grade === 'premium') && (
          <ContractSmsPanel
            contractId={id}
            contract={contract}
            isAdmin={isAdmin}
          />
        )}
      </div>

      {/* ─── 원본 이미지 전체화면 뷰어 ─── */}
      {viewerIdx !== null && Array.isArray(contract.image_url) && (
        <div
          onClick={() => setViewerIdx(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <button
            onClick={() => setViewerIdx(null)}
            style={{
              position: 'absolute', top: '20px', right: '20px',
              backgroundColor: 'rgba(255,255,255,0.9)', border: 'none',
              borderRadius: '50%', width: '40px', height: '40px',
              cursor: 'pointer', fontSize: '18px',
            }}
          >
            X
          </button>

          {viewerIdx > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setViewerIdx(viewerIdx - 1); }}
              style={{
                position: 'absolute', left: '20px',
                backgroundColor: 'rgba(255,255,255,0.9)', border: 'none',
                borderRadius: '50%', width: '44px', height: '44px',
                cursor: 'pointer', fontSize: '20px',
              }}
            >
              &lt;
            </button>
          )}

          <img
            src={contract.image_url[viewerIdx]}
            alt={'계약서 ' + (viewerIdx + 1) + '페이지'}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }}
          />

          {viewerIdx < contract.image_url.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setViewerIdx(viewerIdx + 1); }}
              style={{
                position: 'absolute', right: '20px',
                backgroundColor: 'rgba(255,255,255,0.9)', border: 'none',
                borderRadius: '50%', width: '44px', height: '44px',
                cursor: 'pointer', fontSize: '20px',
              }}
            >
              &gt;
            </button>
          )}

          <div style={{
            position: 'absolute', bottom: '20px', color: 'white', fontSize: '14px',
            backgroundColor: 'rgba(0,0,0,0.5)', padding: '6px 14px', borderRadius: '14px',
          }}>
            {viewerIdx + 1} / {contract.image_url.length}
          </div>
        </div>
      )}


    </div>
  );
}
